import React, { ChangeEventHandler, CSSProperties, FC, MouseEventHandler, useEffect, useRef } from 'react';
import { isEmpty } from 'ramda';
import { i18nSortSelectOptions, isBmiSlide } from '@/form/utils.tsx';
import { useEventConfig } from '@/eventConfig/useEventConfig.ts';
import { OptionGroup } from './Optiongroup.tsx';
import { Option } from './Option.tsx';
import { ConfigField, ConfigSlideId, SelectFieldOption, Slide } from '@/eventConfig/types.ts';
import ReactMarkdown from 'react-markdown';
import { translate } from '@/translation/translate.ts';
import { normalize } from '@/form/fields/_pipeline/normalize.ts';
import { fromTemplate } from '@/translation/fromTemplate.tsx';
import { FormGetFieldStatusFn, FormGetFieldValueFn, FormOnChangeFn } from '@/form/FormProvider.tsx';
import { useUiEvent } from '@/util/useUiEvent.ts';
import useSlideIcons from '@/util/slideIcons.ts';
import { useFieldValidation } from '../_pipeline/useValidateField.ts';
import { useBmiValidation } from '@/util/useBmiValidation.ts';
import { useRevalidateField } from '@/util/useRevalidateField.ts';

export interface SelectProps {
  field: ConfigField;
  slideId: ConfigSlideId;
  onChange: FormOnChangeFn;
  getFieldStatus: FormGetFieldStatusFn;
  getFieldValue: FormGetFieldValueFn;
}

export const resolveSelectOptions = (
  field: ConfigField,
  defaultLocale: string,
  getFieldStatus: FormGetFieldStatusFn,
): { options: SelectFieldOption[]; hasNoOptions: boolean } => {
  let options = i18nSortSelectOptions((field.options ?? []) as SelectFieldOption[], defaultLocale);
  let hasNoOptions = options.length <= 0;

  // Linked fields can override the options rendered here.
  if (field.link) {
    const [_slideId, fieldName] = (field.link.field ?? '').split('.');
    const linkedValue = getFieldStatus(fieldName)?.value?.normalized;

    const linkedConfiguration = options.find((e) => String(e.value || e.label) === String(linkedValue));
    const emptyOptions = field.link.emptyOptions || [];

    options = linkedConfiguration ? (linkedConfiguration.options ?? emptyOptions) : emptyOptions;
    hasNoOptions = options === emptyOptions;
  }

  return { options, hasNoOptions };
};

export const SelectInput: FC<SelectProps> = ({ field, slideId, getFieldStatus, onChange, getFieldValue }) => {
  const { eventConfig } = useEventConfig();
  const { handleUiEvent } = useUiEvent();
  const { infoIcon } = useSlideIcons({} as Slide);
  const { validateField } = useFieldValidation(getFieldValue, getFieldStatus);
  const { isValidBmi } = useBmiValidation(getFieldValue);
  const { revalidateFields } = useRevalidateField(validateField);

  const fieldStatus = getFieldStatus(field.name);

  const fieldHint = fieldStatus?.hint;
  const isValid = fieldStatus?.isValid ?? true;
  const value = String(fieldStatus?.value?.raw ?? '');
  const prev = useRef<string>(value);
  const htmlId = field.name; // The <Select> takes only the name as ID. Maybe unify this?
  const validators = field.validator || [];
  const isRequired = validators.find((v) => v.type === 'required' || v.type === 'required_if');

  const { options, hasNoOptions } = resolveSelectOptions(field, eventConfig!.meta.defaultLocale, getFieldStatus);

  useEffect(() => {
    if (field.revalidate && value != prev.current) {
      prev.current = value;
      revalidateFields(field.revalidate);
    }
  }, [field.revalidate, revalidateFields, value]);

  useEffect(() => {
    const e = { target: { value: value } } as React.ChangeEvent<HTMLSelectElement>;
    if (isBmiSlide(slideId)) handleChange(e);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidBmi]);

  useEffect(() => {
    const gfs = getFieldStatus(field.name);
    const fieldValue = gfs?.value ?? { raw: null, normalized: null };
    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleLabelClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    if (field.click && handleUiEvent) {
      handleUiEvent(field.click);
    }
  };

  const handleChange: ChangeEventHandler<HTMLSelectElement> = (e) => {
    const raw = e.target.value ?? '';
    const normalized = normalize(field.datatype, raw);
    const fieldValue = { raw, normalized };

    const { hint, isValid } = validateField(field.validator, fieldValue);
    onChange(field.name, { value: fieldValue, hint, isValid, slideId });
  };
  const cssClasses = [
    'field',
    'field-' + field.name,
    'layout-' + (field.layout || 'default'),
    'col-' + (field.width || '12'),
    'datatype-' + (field.datatype || 'text').split(':')[0],
    'state-' + (isValid ? 'valid' : 'invalid'),
  ];

  const hint = fromTemplate(fieldHint || field?.hint || '');
  const hasInfoLayer = !!field.info;
  const handleInfoClick: MouseEventHandler<HTMLLabelElement> = (_e) => {
    handleUiEvent({
      type: 'layer',
      text: field?.info?.text,
      title: field?.info?.title,
    });
  };

  const infoIconStyles: CSSProperties = {
    position: 'unset',
    marginRight: '0.5rem',
    cursor: 'pointer',
  };

  return (
    <div className={cssClasses.join(' ')}>
      <div className={'input-' + field.type}>
        <label htmlFor={htmlId} className={isRequired ? 'required' : ''} onClick={handleLabelClick}>
          {hasInfoLayer && (
            <span onClick={handleInfoClick}>
              <i className={infoIcon} style={infoIconStyles} />
            </span>
          )}
          <ReactMarkdown unwrapDisallowed={true} allowedElements={['a']}>
            {translate(field.label)}
          </ReactMarkdown>
        </label>

        <select
          value={value}
          className={!isEmpty(value) ? 'populated' : 'empty'}
          disabled={hasNoOptions}
          name={field.name}
          onChange={handleChange}
          id={htmlId}
        >
          {options.map((selectOption, index) => {
            const { items, value } = selectOption;
            if (!value) {
              if (items?.length) {
                const children = items?.map((item) => {
                  if (item.value == '-') {
                    return <hr key={item.value ? item.value.toString() + index : index} />;
                  } else {
                    return (
                      <Option
                        key={item.value ? item.value.toString() + index : index}
                        selectOption={item}
                        value={value}
                      />
                    );
                  }
                });

                return (
                  <OptionGroup
                    key={`OptGroupChildren_${value ? value.toString() + index : index}`}
                    selectOption={selectOption}
                  >
                    {children}
                  </OptionGroup>
                );
              }
              return (
                <Option
                  key={`OptEmpty_${value ? value.toString() + index : index}`}
                  selectOption={selectOption}
                  value=""
                />
              );
            }
            if (value == '--') {
              return <hr key={`Opt_${value ? value.toString() + index : index}`} />;
            } else {
              return (
                <Option
                  key={`Opt_${value ? value.toString() + index : index}`}
                  selectOption={selectOption}
                  value={value}
                />
              );
            }
          })}
        </select>
        <i className="fas fa-list" />
      </div>
      <div className={hint ? 'hint message' : 'hint'}>{hint}</div>
    </div>
  );
};
